import { onAuthStateChanged, type User as FirebaseUser } from "firebase/auth";
import { getSession } from "next-auth/react";
import { getFirebaseAuth } from "@/lib/api/firebase-ultra-safe";
import {
  ensureFirebaseAuthFromNextAuth,
  resolveNextAuthUserId,
} from "@/lib/auth/ensureFirebaseAuthFromNextAuth";

/** Firebase Auth 세션 복원까지 대기 (useAuth user와 currentUser 타이밍 차이 보완) */
export async function waitForFirebaseUser(
  timeoutMs = 12000
): Promise<FirebaseUser> {
  const auth = getFirebaseAuth();
  if (!auth) {
    throw new Error("Firebase Auth를 사용할 수 없습니다.");
  }

  if (auth.currentUser) {
    return auth.currentUser;
  }

  try {
    const session = await getSession();
    const uid = session ? resolveNextAuthUserId(session) : null;
    if (uid) {
      return await ensureFirebaseAuthFromNextAuth(uid);
    }
  } catch (error) {
    const message =
      error instanceof Error ? error.message : "Google 로그인 연동 실패";
    throw new Error(message);
  }

  if (auth.currentUser) {
    return auth.currentUser;
  }

  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      unsubscribe();
      reject(
        new Error(
          "로그인 세션을 확인하지 못했습니다. 로그아웃 후 다시 로그인해 주세요."
        )
      );
    }, timeoutMs);

    const unsubscribe = onAuthStateChanged(auth, user => {
      if (!user) return;
      window.clearTimeout(timer);
      unsubscribe();
      resolve(user);
    });
  });
}
